import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAutosave, SaveState, TextField, SelectField } from "@/components/Form";
import { PageHeader } from "@/components/Badges";
import { ConsentNotice } from "@/pages/app/Onboarding";
import { LEGAL_FORMS, NATIONS, ENGLAND_REGIONS, SECTORS, TRADING_STAGES, fmtDateTime } from "@/lib/format";

const Members = ({ org, reload }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orgs/${org.id}/members`, { email, role });
      setEmail("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (em) => {
    try { await api.delete(`/orgs/${org.id}/members/${encodeURIComponent(em)}`); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="card p-6" data-testid="members-section">
      <h2 className="font-semibold text-[#0B192C]">Colleagues and permissions</h2>
      <p className="mt-1 text-xs text-slate-500">Owners approve and submit. Editors prepare drafts. Viewers can read only. Invitees sign in with Google using the invited email.</p>
      <ul className="mt-4 divide-y divide-slate-100">
        {org.members.map((m) => (
          <li key={m.email} className="flex items-center justify-between py-2 text-sm"><span>{m.email} <span className="caption ml-2">{m.role}</span></span>
            {org._role === "owner" && <button onClick={() => remove(m.email)} data-testid={`remove-member-${m.email}`} className="text-xs text-red-600 hover:underline">Remove</button>}</li>
        ))}
      </ul>
      {org._role === "owner" && (
        <form onSubmit={invite} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input type="email" required aria-label="Colleague email" data-testid="invite-email-input" placeholder="colleague@company.co.uk" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select aria-label="Role" data-testid="invite-role-select" className="input sm:w-36" value={role} onChange={(e) => setRole(e.target.value)}><option value="editor">Editor</option><option value="viewer">Viewer</option><option value="owner">Owner</option></select>
          <button data-testid="invite-submit-button" className="btn-teal btn-sm">Invite</button>
        </form>
      )}
    </section>
  );
};

const Activity = ({ orgId }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/orgs/${orgId}/activity`).then(({ data }) => setRows(data));
  }, [orgId]);
  return (
    <section className="card p-6" data-testid="activity-log">
      <h2 className="font-semibold text-[#0B192C]">Activity log</h2>
      <ul className="mt-3 max-h-72 divide-y divide-slate-100 overflow-auto text-xs">
        {rows.map((r) => <li key={r.id} className="py-2"><span className="font-mono text-slate-500">{fmtDateTime(r.at)}</span> · {r.email} · <b>{r.action.replace(/_/g, " ")}</b> {r.detail}</li>)}
      </ul>
    </section>
  );
};

export default function Business() {
  const { orgId } = useAuth();
  const [org, setOrg] = useState(null);
  const load = useCallback(() => api.get(`/orgs/${orgId}`).then(({ data }) => setOrg(data)), [orgId]);
  useEffect(() => {
    if (orgId) load();
  }, [orgId, load]);
  const save = useCallback(async (body) => { const { data } = await api.put(`/orgs/${orgId}`, body); setOrg((o) => ({ ...o, provenance: data.provenance })); }, [orgId]);
  const { values, set, state } = useAutosave(org, save);
  if (!org) return <p className="text-sm text-slate-500">Loading…</p>;
  const p = { values, set, prov: org.provenance };
  return (
    <div className="space-y-6 fade-up">
      <PageHeader title="Business profile" sub="Entered once and reused across every project and application."><SaveState state={state} /></PageHeader>
      <section className="card grid gap-5 p-6 sm:grid-cols-2" data-testid="business-profile-form">
        <TextField name="name" label="Business name" {...p} />
        <SelectField name="legal_form" label="Legal form" options={LEGAL_FORMS} {...p} />
        <TextField name="company_number" label="Company number (if incorporated)" {...p} />
        <SelectField name="nation" label="UK nation" options={NATIONS} {...p} />
        {values.nation === "England" && <SelectField name="region" label="English region" options={ENGLAND_REGIONS} {...p} />}
        <TextField name="postcode" label="Postcode" {...p} />
        <SelectField name="sector" label="Sector" options={SECTORS} {...p} />
        <SelectField name="trading_stage" label="Trading stage" options={TRADING_STAGES} {...p} />
        <TextField name="turnover" label="Annual turnover (£)" {...p} />
        <TextField name="employee_count" label="Employee count" type="number" {...p} />
        <TextField name="funding_requirement" label="Funding requirement (£)" {...p} />
        <TextField name="ownership" label="Ownership" hint="Who owns the business and in what shares." area {...p} />
        <TextField name="team_summary" label="Team" hint="Key people, roles and relevant experience." area {...p} />
        <TextField name="previous_funding" label="Previous funding" hint="Grants, loans, investment or subsidies already received, with dates and amounts." area {...p} />
      </section>
      <ConsentNotice orgId={org.id} current={org.consent} onDone={load} />
      <Members org={org} reload={load} />
      <Activity orgId={org.id} />
    </div>
  );
}
